import { useState } from 'react'
import {
  X, BookOpen, Rocket, GitBranch, Upload,
  Code2, Wand2, Network, Boxes, Database,
  Search, Building2, TestTube, Package, HelpCircle,
  ChevronRight, Play
} from 'lucide-react'
import './DocsView.css'

// Documentation content organized by section - CORRECT FLOW ORDER
const DOCS_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    content: GettingStartedContent,
  },
  {
    id: 'workflow-overview',
    title: 'Workflow Overview',
    icon: GitBranch,
    content: WorkflowOverviewContent,
  },
  {
    id: 'ingest',
    title: '1. Ingest',
    icon: Upload,
    content: IngestContent,
  },
  {
    id: 'code-analysis',
    title: '2. Code Analysis',
    icon: Code2,
    content: CodeAnalysisContent,
  },
  {
    id: 'code-refactor',
    title: '3. Code Refactor',
    icon: Wand2,
    content: CodeRefactorContent,
  },
  {
    id: 'dependency-mapper',
    title: '4. Dependency Mapper',
    icon: Network,
    content: DependencyMapperContent,
  },
  {
    id: 'monolith-identifier',
    title: '5. Monolith Identifier',
    icon: Boxes,
    content: MonolithIdentifierContent,
  },
  {
    id: 'data-analysis',
    title: '6. Data Analysis',
    icon: Database,
    content: DataAnalysisContent,
  },
  {
    id: 'discovery',
    title: '7. Discovery & ROI',
    icon: Search,
    content: DiscoveryContent,
  },
  {
    id: 'architecture',
    title: '8. Architecture',
    icon: Building2,
    content: ArchitectureContent,
  },
  {
    id: 'smart-tests',
    title: '9. Smart Tests',
    icon: TestTube,
    content: SmartTestsContent,
  },
  {
    id: 'java-packages',
    title: '10. Java Packages',
    icon: Package,
    content: JavaPackagesContent,
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: HelpCircle,
    content: TroubleshootingContent,
  },
]

function DocsView({ isOpen, onClose }) {
  const [activeSection, setActiveSection] = useState('getting-started')

  if (!isOpen) return null

  const currentSection = DOCS_SECTIONS.find(s => s.id === activeSection)
  const ContentComponent = currentSection?.content

  return (
    <div className="docs-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="docs-container">
        {/* Header */}
        <div className="docs-header">
          <div className="docs-header-title">
            <BookOpen size={24} />
            <h1>Documentation</h1>
          </div>
          <button className="docs-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="docs-body">
          {/* Sidebar Navigation */}
          <nav className="docs-sidebar">
            {DOCS_SECTIONS.map((section) => {
              const Icon = section.icon
              return (
                <button
                  key={section.id}
                  className={`docs-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <Icon size={18} />
                  <span>{section.title}</span>
                  <ChevronRight size={14} className="nav-arrow" />
                </button>
              )
            })}
          </nav>

          {/* Content Area */}
          <main className="docs-content">
            {ContentComponent && <ContentComponent />}
          </main>
        </div>
      </div>
    </div>
  )
}

// ============================================
// Content Components
// ============================================

function GettingStartedContent() {
  return (
    <article className="docs-article">
      <h1>Getting Started with RecodeIT</h1>
      <p className="lead">
        RecodeIT transforms legacy COBOL applications into modern Java/Spring Boot
        applications through an AI-powered multi-stage pipeline.
      </p>

      <div className="tip-box">
        <Play size={20} />
        <div>
          <strong>Recommended: Use the Full Pipeline Template</strong>
          <p>
            For most users, we recommend using the <strong>Templates → Full Pipeline</strong> workflow.
            This runs all 10 steps in the correct order automatically.
          </p>
        </div>
      </div>

      <h2>Quick Start (Full Pipeline)</h2>
      <ol className="steps-list">
        <li>
          <strong>Create a Project</strong>
          <p>Click "New Project", select your account, and give your project a name.</p>
        </li>
        <li>
          <strong>Select Full Pipeline Template</strong>
          <p>In the workflow designer, choose Templates → Full Pipeline to load the complete workflow.</p>
        </li>
        <li>
          <strong>Upload Your COBOL Files</strong>
          <p>The first step (Ingest) prompts you to upload a ZIP containing your COBOL programs and copybooks.</p>
        </li>
        <li>
          <strong>Click Run</strong>
          <p>The pipeline executes all 10 steps automatically: Analysis → Refactor → Dependencies → and so on.</p>
        </li>
        <li>
          <strong>Download Your Java Package</strong>
          <p>When complete, download your Spring Boot application from the Java Packages step.</p>
        </li>
      </ol>

      <h2>The 10-Step Pipeline</h2>
      <div className="pipeline-vertical">
        <div className="pipeline-step-v"><span>1</span> Ingest (Upload Files)</div>
        <div className="pipeline-step-v"><span>2</span> Code Analysis</div>
        <div className="pipeline-step-v"><span>3</span> Code Refactor</div>
        <div className="pipeline-step-v"><span>4</span> Dependency Mapper</div>
        <div className="pipeline-step-v"><span>5</span> Monolith Identifier</div>
        <div className="pipeline-step-v"><span>6</span> Data Analysis</div>
        <div className="pipeline-step-v"><span>7</span> Discovery & ROI</div>
        <div className="pipeline-step-v"><span>8</span> Architecture</div>
        <div className="pipeline-step-v"><span>9</span> Smart Tests</div>
        <div className="pipeline-step-v"><span>10</span> Java Packages</div>
      </div>

      <h2>Key Concepts</h2>
      <dl className="concept-list">
        <dt>Account</dt>
        <dd>Your organization or tenant. All projects belong to an account.</dd>

        <dt>Application / Project</dt>
        <dd>A modernization project containing your COBOL files and generated artifacts.</dd>

        <dt>Workflow</dt>
        <dd>The visual pipeline of transformation steps. Use templates or build custom flows.</dd>

        <dt>Job</dt>
        <dd>An execution of a workflow step. Jobs run asynchronously and can be monitored.</dd>
      </dl>
    </article>
  )
}

function WorkflowOverviewContent() {
  return (
    <article className="docs-article">
      <h1>Workflow Overview</h1>
      <p className="lead">
        The RecodeIT workflow transforms COBOL to Java through 10 specialized steps,
        each providing unique insights and transformations.
      </p>

      <div className="tip-box">
        <Play size={20} />
        <div>
          <strong>Use Templates</strong>
          <p>
            Go to <strong>Templates → Full Pipeline</strong> to load a pre-configured workflow
            with all steps connected in the correct order.
          </p>
        </div>
      </div>

      <h2>Complete Pipeline Flow</h2>
      <div className="flow-diagram">
        <pre>{`
  ┌──────────────┐
  │   INGEST     │  Upload COBOL ZIP files
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │CODE ANALYSIS │  Parse COBOL, extract structure
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │CODE REFACTOR │  Improve & modernize patterns
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │ DEPENDENCY   │  Build call graphs & metrics
  │   MAPPER     │
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │  MONOLITH    │  Identify microservice boundaries
  │ IDENTIFIER   │
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │DATA ANALYSIS │  ERD, data lineage, schemas
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │ DISCOVERY &  │  ROI calculation, business rules
  │     ROI      │
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │ ARCHITECTURE │  AWS recommendations, IaC
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │ SMART TESTS  │  Generate JUnit test cases
  └──────┬───────┘
         ▼
  ┌──────────────┐
  │JAVA PACKAGES │  Download Spring Boot ZIP
  └──────────────┘
        `}</pre>
      </div>

      <h2>Step Summary</h2>
      <table className="docs-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Step</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>1</td><td><strong>Ingest</strong></td><td>Upload and organize COBOL source files</td></tr>
          <tr><td>2</td><td><strong>Code Analysis</strong></td><td>Parse COBOL structure, generate initial Java</td></tr>
          <tr><td>3</td><td><strong>Code Refactor</strong></td><td>Apply Spring Boot patterns, improve code quality</td></tr>
          <tr><td>4</td><td><strong>Dependency Mapper</strong></td><td>Build call graphs, calculate coupling metrics</td></tr>
          <tr><td>5</td><td><strong>Monolith Identifier</strong></td><td>Detect patterns, suggest microservice boundaries</td></tr>
          <tr><td>6</td><td><strong>Data Analysis</strong></td><td>Generate ERD, analyze data lineage</td></tr>
          <tr><td>7</td><td><strong>Discovery & ROI</strong></td><td>Extract business rules, calculate modernization ROI</td></tr>
          <tr><td>8</td><td><strong>Architecture</strong></td><td>Generate AWS architecture recommendations</td></tr>
          <tr><td>9</td><td><strong>Smart Tests</strong></td><td>Generate JUnit test cases for validation</td></tr>
          <tr><td>10</td><td><strong>Java Packages</strong></td><td>Create downloadable Spring Boot application</td></tr>
        </tbody>
      </table>

      <h2>Workflow Designer</h2>
      <p>
        The visual workflow designer lets you drag nodes onto a canvas and connect them.
        Each node represents a transformation step.
      </p>
      <ul>
        <li><strong>Templates:</strong> Use pre-built workflows (recommended)</li>
        <li><strong>Add nodes:</strong> Drag from the node palette on the left</li>
        <li><strong>Connect:</strong> Click and drag from output handle to input handle</li>
        <li><strong>Configure:</strong> Click a node to see its properties</li>
        <li><strong>Execute:</strong> Click "Run" to execute the workflow</li>
      </ul>
    </article>
  )
}

function IngestContent() {
  return (
    <article className="docs-article">
      <h1>Step 1: Ingest</h1>
      <p className="lead">
        The Ingest step uploads your COBOL source files and prepares them for analysis.
      </p>

      <h2>Supported Formats</h2>
      <ul>
        <li><strong>ZIP files</strong> - Containing .cob, .cbl, .cpy files</li>
        <li><strong>Individual files</strong> - Direct COBOL program uploads</li>
      </ul>

      <h2>File Types</h2>
      <table className="docs-table">
        <thead>
          <tr><th>Extension</th><th>Type</th><th>Description</th></tr>
        </thead>
        <tbody>
          <tr><td>.cob, .cbl</td><td>Program</td><td>COBOL program source</td></tr>
          <tr><td>.cpy</td><td>Copybook</td><td>Shared data definitions</td></tr>
          <tr><td>.jcl</td><td>JCL</td><td>Job control language (optional)</td></tr>
        </tbody>
      </table>

      <h2>Best Practices</h2>
      <ul>
        <li>Include all copybooks referenced by your programs</li>
        <li>Use consistent file naming</li>
        <li>Remove binary files or non-source content from ZIP</li>
        <li>Organize files in a flat structure or logical folders</li>
      </ul>

      <h2>What Happens</h2>
      <ol>
        <li>ZIP file is uploaded to the server</li>
        <li>Contents are extracted and validated</li>
        <li>Files are cataloged by type (program, copybook, JCL)</li>
        <li>File inventory is returned for review</li>
      </ol>
    </article>
  )
}

function CodeAnalysisContent() {
  return (
    <article className="docs-article">
      <h1>Step 2: Code Analysis</h1>
      <p className="lead">
        Code Analysis parses your COBOL programs and generates the initial Java translation.
        This is the core transformation engine.
      </p>

      <h2>What It Does</h2>
      <ul>
        <li>Parses COBOL using tree-sitter for accurate syntax understanding</li>
        <li>Extracts DATA DIVISION (working storage, files, records)</li>
        <li>Extracts PROCEDURE DIVISION (paragraphs, sections, logic)</li>
        <li>Generates equivalent Java code with line-by-line traceability</li>
        <li>Creates dependency graphs showing program flow</li>
      </ul>

      <h2>Output Artifacts</h2>
      <ul>
        <li><strong>Java source code</strong> - Generated .java files</li>
        <li><strong>Maven project</strong> - Complete pom.xml and structure</li>
        <li><strong>Parse trees</strong> - JSON representation of COBOL structure</li>
        <li><strong>Call graphs</strong> - PNG visualization of program flow</li>
      </ul>

      <h2>COBOL to Java Mapping</h2>
      <table className="docs-table">
        <thead>
          <tr><th>COBOL</th><th>Java</th></tr>
        </thead>
        <tbody>
          <tr><td>PROGRAM-ID</td><td>Class name</td></tr>
          <tr><td>PARAGRAPH</td><td>Method</td></tr>
          <tr><td>WORKING-STORAGE</td><td>Instance variables</td></tr>
          <tr><td>PERFORM</td><td>Method call</td></tr>
          <tr><td>MOVE</td><td>Assignment</td></tr>
          <tr><td>IF/EVALUATE</td><td>if/switch</td></tr>
        </tbody>
      </table>

      <h2>Line Traceability</h2>
      <p>
        Every generated Java line includes a <code>// L:nnn</code> comment showing
        the original COBOL line number for full traceability.
      </p>
    </article>
  )
}

function CodeRefactorContent() {
  return (
    <article className="docs-article">
      <h1>Step 3: Code Refactor</h1>
      <p className="lead">
        Code Refactor improves the generated Java by applying modern patterns
        and Spring Boot best practices.
      </p>

      <h2>What It Does</h2>
      <ul>
        <li>Applies Spring Boot annotations and patterns</li>
        <li>Improves code readability and maintainability</li>
        <li>Adds proper exception handling</li>
        <li>Implements dependency injection patterns</li>
        <li>Optimizes data access patterns</li>
      </ul>

      <h2>Transformations Applied</h2>
      <table className="docs-table">
        <thead>
          <tr><th>Before</th><th>After</th></tr>
        </thead>
        <tbody>
          <tr><td>Raw JDBC calls</td><td>Spring Data JPA</td></tr>
          <tr><td>Manual instantiation</td><td>@Autowired injection</td></tr>
          <tr><td>Procedural code</td><td>Service layer pattern</td></tr>
          <tr><td>Hardcoded values</td><td>@Value configuration</td></tr>
        </tbody>
      </table>

      <h2>Output</h2>
      <ul>
        <li><strong>Refactored Java files</strong> - Improved code quality</li>
        <li><strong>Spring configuration</strong> - application.properties</li>
        <li><strong>Refactoring report</strong> - Changes made and rationale</li>
      </ul>
    </article>
  )
}

function DependencyMapperContent() {
  return (
    <article className="docs-article">
      <h1>Step 4: Dependency Mapper</h1>
      <p className="lead">
        Dependency Mapper analyzes relationships between programs, creating
        visual call graphs and calculating coupling metrics.
      </p>

      <h2>What It Analyzes</h2>
      <ul>
        <li><strong>CALL statements</strong> - Inter-program calls</li>
        <li><strong>COPY statements</strong> - Copybook dependencies</li>
        <li><strong>PERFORM statements</strong> - Internal paragraph calls</li>
        <li><strong>File I/O</strong> - Data dependencies</li>
      </ul>

      <h2>Output</h2>
      <ul>
        <li><strong>Call Graph (PNG)</strong> - Visual diagram of relationships</li>
        <li><strong>Dependency Matrix</strong> - Tabular view of all dependencies</li>
        <li><strong>Coupling Metrics</strong> - Afferent/efferent coupling scores</li>
        <li><strong>Cluster Analysis</strong> - Groups of related programs</li>
      </ul>

      <h2>Metrics Explained</h2>
      <dl className="concept-list">
        <dt>Afferent Coupling (Ca)</dt>
        <dd>Programs that depend on this program. High = widely used.</dd>

        <dt>Efferent Coupling (Ce)</dt>
        <dd>Programs this depends on. High = high dependency.</dd>

        <dt>Instability (I)</dt>
        <dd>Ce / (Ca + Ce). Higher = more likely to need changes.</dd>
      </dl>
    </article>
  )
}

function MonolithIdentifierContent() {
  return (
    <article className="docs-article">
      <h1>Step 5: Monolith Identifier</h1>
      <p className="lead">
        Monolith Identifier analyzes your application to suggest how it could be
        decomposed into microservices.
      </p>

      <h2>What It Does</h2>
      <ul>
        <li>Identifies bounded contexts in your codebase</li>
        <li>Detects natural service boundaries</li>
        <li>Analyzes data ownership patterns</li>
        <li>Suggests microservice decomposition strategies</li>
      </ul>

      <h2>Analysis Factors</h2>
      <ul>
        <li><strong>Coupling analysis</strong> - Which programs are tightly coupled</li>
        <li><strong>Cohesion analysis</strong> - Which programs belong together</li>
        <li><strong>Data affinity</strong> - Which programs share data</li>
        <li><strong>Business domains</strong> - Logical business groupings</li>
      </ul>

      <h2>Output</h2>
      <ul>
        <li><strong>Decomposition strategy</strong> - Recommended service boundaries</li>
        <li><strong>Service diagrams</strong> - Visual representation of proposed services</li>
        <li><strong>Migration roadmap</strong> - Suggested order for extraction</li>
      </ul>
    </article>
  )
}

function DataAnalysisContent() {
  return (
    <article className="docs-article">
      <h1>Step 6: Data Analysis</h1>
      <p className="lead">
        Data Analysis examines your data structures to generate ERDs,
        understand data lineage, and recommend database schemas.
      </p>

      <h2>What It Analyzes</h2>
      <ul>
        <li><strong>FILE SECTION</strong> - File definitions and record layouts</li>
        <li><strong>WORKING-STORAGE</strong> - Data structures and variables</li>
        <li><strong>COPY statements</strong> - Shared data definitions</li>
        <li><strong>Data flow</strong> - How data moves through programs</li>
      </ul>

      <h2>Output</h2>
      <ul>
        <li><strong>Entity-Relationship Diagram</strong> - Visual data model</li>
        <li><strong>Data dictionary</strong> - Field definitions and types</li>
        <li><strong>Data lineage</strong> - Where data originates and flows</li>
        <li><strong>Schema recommendations</strong> - Suggested database design</li>
      </ul>

      <h2>Database Mapping</h2>
      <table className="docs-table">
        <thead>
          <tr><th>COBOL</th><th>Database</th></tr>
        </thead>
        <tbody>
          <tr><td>FD (File Description)</td><td>Table</td></tr>
          <tr><td>01 Level Record</td><td>Row structure</td></tr>
          <tr><td>Elementary items</td><td>Columns</td></tr>
          <tr><td>OCCURS</td><td>Related table or JSON array</td></tr>
        </tbody>
      </table>
    </article>
  )
}

function DiscoveryContent() {
  return (
    <article className="docs-article">
      <h1>Step 7: Discovery & ROI</h1>
      <p className="lead">
        Discovery extracts business value from your code, calculating ROI
        and identifying business rules embedded in the logic.
      </p>

      <h2>What It Analyzes</h2>
      <ul>
        <li><strong>Complexity metrics</strong> - Lines of code, cyclomatic complexity</li>
        <li><strong>Business rules</strong> - Embedded calculations, validations</li>
        <li><strong>Business processes</strong> - High-level workflows</li>
        <li><strong>Technical debt</strong> - Maintenance indicators</li>
      </ul>

      <h2>ROI Calculation</h2>
      <p>
        Estimates return on investment based on:
      </p>
      <ul>
        <li>Current maintenance costs</li>
        <li>Technical debt indicators</li>
        <li>Transformation complexity</li>
        <li>Expected productivity gains</li>
      </ul>

      <h2>Business Rule Extraction</h2>
      <p>Identifies embedded business logic:</p>
      <ul>
        <li>Calculation formulas (interest, taxes, fees)</li>
        <li>Validation rules (date checks, range limits)</li>
        <li>Conditional processing (eligibility, routing)</li>
        <li>Transformation rules (data format changes)</li>
      </ul>
    </article>
  )
}

function ArchitectureContent() {
  return (
    <article className="docs-article">
      <h1>Step 8: Architecture</h1>
      <p className="lead">
        Architecture generates AWS cloud recommendations and infrastructure-as-code
        for your modernized application.
      </p>

      <h2>Recommendations Include</h2>
      <ul>
        <li><strong>Compute</strong> - ECS, Lambda, or EC2 recommendations</li>
        <li><strong>Database</strong> - RDS, DynamoDB, or Aurora suggestions</li>
        <li><strong>Storage</strong> - S3 configuration for files</li>
        <li><strong>Integration</strong> - API Gateway, SQS, SNS patterns</li>
      </ul>

      <h2>Factors Considered</h2>
      <ul>
        <li>Application size and complexity</li>
        <li>Data volume and access patterns</li>
        <li>Batch vs. online processing needs</li>
        <li>Integration requirements</li>
      </ul>

      <h2>Output Artifacts</h2>
      <ul>
        <li><strong>Architecture diagram</strong> - Visual AWS design</li>
        <li><strong>Service selection</strong> - Detailed recommendations</li>
        <li><strong>Cost estimates</strong> - Monthly running costs</li>
        <li><strong>Infrastructure as Code</strong> - Terraform/CloudFormation</li>
      </ul>
    </article>
  )
}

function SmartTestsContent() {
  return (
    <article className="docs-article">
      <h1>Step 9: Smart Tests</h1>
      <p className="lead">
        Smart Tests generates JUnit test cases to validate your modernized application
        behaves correctly.
      </p>

      <h2>What It Generates</h2>
      <ul>
        <li><strong>Unit tests</strong> - Method-level test cases</li>
        <li><strong>Integration tests</strong> - Service interaction tests</li>
        <li><strong>Test data</strong> - Sample input/output data</li>
        <li><strong>Coverage mapping</strong> - Tests mapped to COBOL paragraphs</li>
      </ul>

      <h2>Test Categories</h2>
      <table className="docs-table">
        <thead>
          <tr><th>Category</th><th>Purpose</th></tr>
        </thead>
        <tbody>
          <tr><td>Happy path</td><td>Normal execution flow</td></tr>
          <tr><td>Boundary tests</td><td>Edge cases and limits</td></tr>
          <tr><td>Error handling</td><td>Exception scenarios</td></tr>
          <tr><td>Data validation</td><td>Input validation rules</td></tr>
        </tbody>
      </table>

      <h2>Output</h2>
      <ul>
        <li><strong>JUnit test classes</strong> - Ready to run with Maven</li>
        <li><strong>Test fixtures</strong> - Sample data files</li>
        <li><strong>Coverage report</strong> - What's tested</li>
      </ul>
    </article>
  )
}

function JavaPackagesContent() {
  return (
    <article className="docs-article">
      <h1>Step 10: Java Packages</h1>
      <p className="lead">
        The final step packages everything into a downloadable Spring Boot application.
      </p>

      <h2>What You Get</h2>
      <ul>
        <li><strong>Spring Boot project</strong> - Ready to run with Maven</li>
        <li><strong>Generated Java classes</strong> - Your COBOL logic translated</li>
        <li><strong>Test cases</strong> - Generated JUnit tests</li>
        <li><strong>Maven configuration</strong> - pom.xml with dependencies</li>
        <li><strong>README</strong> - Build and run instructions</li>
      </ul>

      <h2>Project Structure</h2>
      <div className="code-block">
        <pre>{`
your-app/
├── pom.xml
├── README.md
├── src/
│   ├── main/java/
│   │   └── com/modernizeit/generated/
│   │       └── YourProgram.java
│   └── test/java/
│       └── com/modernizeit/generated/
│           └── YourProgramTest.java
└── application.properties
        `}</pre>
      </div>

      <h2>Building the Project</h2>
      <div className="code-block">
        <pre>{`
# Extract the ZIP
unzip your-app.zip
cd your-app

# Compile
mvn compile

# Run tests
mvn test

# Package as JAR
mvn package

# Run the application
java -jar target/your-app.jar
        `}</pre>
      </div>

      <h2>Next Steps After Download</h2>
      <ol>
        <li>Review generated code for accuracy</li>
        <li>Implement any stub methods for external calls</li>
        <li>Add additional unit tests</li>
        <li>Configure for your target environment</li>
        <li>Deploy to AWS (use Architecture recommendations)</li>
      </ol>
    </article>
  )
}

function TroubleshootingContent() {
  return (
    <article className="docs-article">
      <h1>Troubleshooting</h1>
      <p className="lead">
        Common issues and how to resolve them.
      </p>

      <h2>Ingest Issues</h2>
      <dl className="troubleshooting-list">
        <dt>ZIP file won't upload</dt>
        <dd>
          Check file size (max 500MB). Ensure it's a valid ZIP archive.
          Try re-creating the ZIP with standard compression.
        </dd>

        <dt>Files not recognized</dt>
        <dd>
          Ensure COBOL files have .cob, .cbl, or .CBL extensions.
          Copybooks should use .cpy extension.
        </dd>
      </dl>

      <h2>Code Analysis Issues</h2>
      <dl className="troubleshooting-list">
        <dt>Parse errors</dt>
        <dd>
          Check COBOL syntax. Ensure columns 1-6 are sequence numbers,
          column 7 is indicator, and code starts in column 8.
        </dd>

        <dt>Missing copybooks</dt>
        <dd>
          Ensure all COPY statements reference files in your upload.
          Check copybook names match exactly.
        </dd>

        <dt>Java compilation errors</dt>
        <dd>
          Generated Java may have stubs for external CALLs.
          Implement these stubs or remove if not needed.
        </dd>
      </dl>

      <h2>Performance Issues</h2>
      <dl className="troubleshooting-list">
        <dt>Pipeline taking too long</dt>
        <dd>
          Large codebases take longer. Monitor job progress in the status panel.
          Consider analyzing subsets first for testing.
        </dd>
      </dl>

      <h2>Getting Help</h2>
      <ul>
        <li>Check job logs for detailed error messages</li>
        <li>Review analysis artifacts for clues</li>
        <li>Contact support with your job ID</li>
      </ul>
    </article>
  )
}

export default DocsView
